import { minutesToRequestResendCallback, secondsForRecurrentCallCallback, minutesToExpireLinkCallback, minutesToHelpCallback } from "./callbacks";
import logger from 'src/utils/logger/logger';

export const MINUTES_TO_REQUEST_RESENT = 'minutes_to_request_resent';
export const MINUTES_TO_EXPIRE_LINK = 'minutes_to_expire_link';
export const SECONDS_FOR_RECURRENT_CALL = 'seconds_for_recurrent_call';
export const MINUTES_TO_HELP = 'minutes_to_help';


export interface DataToShowTicket {
    showTicket: (dataBuyconfirm: any, groupId: string, islive: boolean, is2FA: boolean, paramsMetrics2FA: any) => void;
    groupId: string;
    islive: boolean;
    worflowstart: object;
}


export type TimerType = typeof MINUTES_TO_REQUEST_RESENT | typeof MINUTES_TO_EXPIRE_LINK | typeof SECONDS_FOR_RECURRENT_CALL | typeof MINUTES_TO_HELP;

export type TimerStatus = 'running' | 'paused' | 'stopped' | 'completed'

export interface Timer {
    id: TimerType;
    name: string;
    type: 'timeout' | 'interval';
    duration: number;
    remaining: number;
    status: TimerStatus;
    timerId?: NodeJS.Timeout;
    startTime?: number;
}

export interface TransactionTimers {
    transactionId: string;
    check2FA: string
    timers: Map<TimerType, Timer>;
    dataBuyconfirm: any;
    dataToShowTicket: DataToShowTicket;
    paramsMetrics2FA: any;
}

type TimerCompletetionCallback = (transactionId: string, timerId?: TimerType, check2FA?: string, dataBuyconfirm?: any, dataToShowTicket?: DataToShowTicket, paramsMetrics2FA?: any )  => Promise<void> | void



class TimerManager {
    private transactions = new Map<string, TransactionTimers>();
    private defaultTimerConfiguration: Record<
        TimerType,
        { name: string; duration: number; type: 'timeout' | 'interval', callback: TimerCompletetionCallback }
    > = {
        [MINUTES_TO_REQUEST_RESENT]: { name: 'Minutes to Request Resent', duration: 0, type: 'timeout',  callback: minutesToRequestResendCallback },
        [MINUTES_TO_EXPIRE_LINK]: { name: 'Minutes to Expire Link', duration: 0, type: 'timeout',  callback: minutesToExpireLinkCallback },
        [SECONDS_FOR_RECURRENT_CALL]: { name: 'Seconds for Recurrent Call', duration: 0, type: 'timeout',  callback: secondsForRecurrentCallCallback },
        [MINUTES_TO_HELP]: { name: 'Minutes to Help', duration: 0, type: 'timeout', callback: minutesToHelpCallback }
    }

    setDuration(timerId: TimerType, duration: number) {
        if(!this.defaultTimerConfiguration[timerId]) return
        this.defaultTimerConfiguration[timerId].duration = duration
    }

    //Crea solo los primeros 4 timers para la transaccion
    createTransaction(transactionId: string, check2FA: string, dataBuyconfirm: any, dataToShowTicket: DataToShowTicket, paramsMetrics2FA: any): boolean {
        if (this.transactions.has(transactionId)) return false

        const timers = new Map<TimerType, Timer>();

        // Solo los primeros 4 temporizadores
        const timerKeys: TimerType[] = [
            MINUTES_TO_REQUEST_RESENT,
            MINUTES_TO_EXPIRE_LINK,
            SECONDS_FOR_RECURRENT_CALL
        ];

        timerKeys.forEach((timerType) => {
            const config = this.defaultTimerConfiguration[timerType];
            timers.set(timerType, {
                id: timerType,
                name: config.name,
                type: config.type,
                duration: config.duration,
                remaining: config.duration,
                status: 'running',
            });
        });

        this.transactions.set(transactionId, {
            transactionId,
            check2FA,
            timers,
            dataBuyconfirm,
            dataToShowTicket,
            paramsMetrics2FA
        });

        //Inicia los timers
        timers.forEach((timer, timerId) => this.startTimerInternal(transactionId, timerId));

        return true;
    }

    createTimer(transactionId: string, timerKey: TimerType): boolean {
        const transaction = this.transactions.get(transactionId);
        if (!transaction) return false;

        if (transaction.timers.has(timerKey)) return false;

        const config = this.defaultTimerConfiguration[timerKey];
        if (!config) return false;

        const timer: Timer = {
            id: timerKey,
            name: config.name,
            type: config.type,
            duration: config.duration,
            remaining: config.duration,
            status: 'running',
        };

        transaction.timers.set(timerKey, timer);
        this.startTimerInternal(transactionId, timerKey);
        return true;
    }


    //Inicia un timer especifico
    private startTimerInternal(transactionId: string, timerId: TimerType): boolean {
       const transaction = this.transactions.get(transactionId);
       if(!transaction) return false;

       const timer = transaction.timers.get(timerId);
        if(!timer) return false;

        if(timer.timerId){
            clearTimeout(timer.timerId)
            clearInterval(timer.timerId)
        }

        timer.status = 'running';
        timer.startTime = Date.now();


        if (timer.type === 'timeout'){
            timer.timerId = setTimeout(async () => {
               await this.completeTimer(transactionId, timerId)
            }, timer.remaining)
        }else {
            timer.timerId = setInterval( async () => {
                await this.executeInterval(transactionId, timerId)
            }, timer.duration)
        }

        return true;
    }

    resetTimer(transactionId: string, timerId: TimerType): boolean {
        const transaction = this.transactions.get(transactionId);
        if(!transaction) return false;

        const timer = transaction.timers.get(timerId);
        if(!timer) return false;

        timer.remaining = timer.duration;
        timer.status = 'running';
        timer.timerId = undefined;
        timer.startTime = undefined

        this.startTimerInternal(transactionId, timerId);

        return true
    }

    deleteTransaction(transactionId: string): boolean {
        const transaction = this.transactions.get(transactionId);
        if(!transaction) return false;

        transaction.timers.forEach(timer => {
            if(timer.timerId)(
                clearTimeout(timer.timerId),
                clearInterval(timer.timerId)
            )
        })

        this.transactions.delete(transactionId);
        return true;
    }

    deleteTimer(transactionId: string, timerId: TimerType): boolean {
        const transaction = this.transactions.get(transactionId);
        if(!transaction) return false;

        const timer = transaction.timers.get(timerId);
        if(!timer) return false;

        if(timer.timerId){
            clearTimeout(timer.timerId);
            clearInterval(timer.timerId);
        }

        transaction.timers.delete(timerId);
        return true;
    }

    private async completeTimer(transactionId: string, timerId: TimerType) {
        const transaction = this.transactions.get(transactionId);
        if(!transaction) return false;

        const timer = transaction.timers.get(timerId);
        if(!timer) return false;

        timer.status = 'completed'
        timer.remaining = 0
        timer.timerId = undefined
        timer.startTime = undefined

       const callback = this.defaultTimerConfiguration[timerId].callback;
       if(callback) {
            try {
                await callback(transactionId, timerId, transaction.check2FA, transaction.dataBuyconfirm, transaction.dataToShowTicket,  transaction.paramsMetrics2FA);
            }catch (error) {
                logger.error(`Error executing callback for timer ${timerId} in transaction ${transactionId}:`, error);
            }
       }
    }

    private async executeInterval(transactionId: string, timerId: TimerType){
        const transaction = this.transactions.get(transactionId);
        if(!transaction) return false;

        const timer = transaction.timers.get(timerId);
        if(!timer) return false;
        const callback = this.defaultTimerConfiguration[timerId].callback;
        if(callback) {
            try {
                await callback(transactionId, timerId, transaction.check2FA);
            }catch (error) {
                logger.error(`Error executing callback for timer ${timerId} in transaction ${transactionId}:`, error);
            }
        }
    }
}

const timerManager = new TimerManager();

export default timerManager



// WEBPACK FOOTER //
// ./src/2FA/TimerManager.ts